import { IActivity } from '@/entities/Activity/domain/Activity.domain';
import { cssVars } from '@/styles/Themes/cssVars';
import { radius } from '@/styles/Themes/Metrics';
import styled from 'styled-components';
import Screen from '@/styles/Themes/Screen';
import Text from '@/styles/Themes/Text';
import Palette from '@/styles/Themes/Palette';
import { useEffect } from 'react';
import Script from 'next/script';
import { isProd } from '@/common/app/constants/envs';

type TAnswersProps = {
    activity: IActivity;
    imgUrl: string;
    productUrl: string;
    destinationId: string | number;
    categoryId?: string | number;
};

const Container = styled.div`
    background-color: ${cssVars.bg_white_dark3};
    border-radius: ${radius.small};
    box-shadow: 0px 4px 23px ${cssVars.shadow_normal};
    margin-bottom: 10px;
    padding: 0 20px;
    overflow: hidden;
    position: relative;

    ${Screen.ipad} {
        margin-bottom: 16px;
        padding: 0 30px;
    }
    ${Screen.desktop} {
        margin-bottom: 10px;
        max-width: 1064px;
    }

    .answerbase-widget {
        color: ${cssVars.fg_gray1_white} !important;
        margin-bottom: 60px !important;
    }

    .answerbase-widget h5 {
        ${Text.Full.large.semibold}
    }

    .vote-down img,
    .vote-up img {
        border-radius: 4px;
    }

    .avatar,
    input {
        border-radius: 20px !important;
    }

    input {
        padding-left: 16px !important;
        background-color: transparent !important;
        color: ${cssVars.fg_gray1_white} !important;
    }

    #ab_ask {
        border-radius: 20px !important;
        background: linear-gradient(90deg, #03d9b1 0%, #1cbbd9 100%) !important;
        padding-left: 24px !important;
        padding-right: 24px !important;
    }

    .type {
        color: ${Palette.green.normal} !important;
    }
`;

const Title = styled.div`
    color: ${cssVars.fg_gray1_white};
    ${Text.Full.large.semibold}
    padding: 17px 0;

    ${Screen.desktop} {
        padding: 22px 0;
    }
`;

export const SCRIPT_ID = 'answers-script';
export const INSERT_ID = 'answers';

export const Answers = ({
    activity,
    imgUrl,
    productUrl,
    destinationId,
    categoryId,
}: TAnswersProps) => {
    const productCategoryId = categoryId
        ? `d${destinationId},d${destinationId}c${categoryId}`
        : `d${destinationId}`;

    useEffect(() => {
        document.querySelector(`.answerbase-widget`)?.remove?.();
        document.querySelector(`#${SCRIPT_ID}`)?.remove?.();

        const el = document.createElement('script');
        el.id = SCRIPT_ID;
        el.type = 'text/javascript';
        el.src =
            'https://tripshock.services.answerbase.com/javascript/widget/full-featured-widget.min.js';
        el.async = true;
        el.setAttribute('data-text-title', 'none');
        el.setAttribute('data-product-external-id', activity.id);
        el.setAttribute('data-product-sku', activity.id);
        el.setAttribute('data-product-title', activity.name);
        el.setAttribute('data-product-price', activity.price?.toString());
        el.setAttribute('data-product-url', productUrl);
        el.setAttribute('data-product-description', activity.description || '');
        el.setAttribute('data-product-image-url', imgUrl);
        el.setAttribute('data-insert-after', `#${INSERT_ID}`);
        el.setAttribute('data-product-association-product-category', productCategoryId);

        document.body.appendChild(el);
    }, [
        activity.id,
        activity.name,
        activity.price,
        activity.description,
        productUrl,
        imgUrl,
        productCategoryId,
    ]);

    return (
        <div>
            {!isProd && (
                <Script
                    id="answer-link-script"
                    strategy="beforeInteractive"
                    src="https://tripshock.services.answerbase.com/javascript/widget/cta-widget.min.js"
                    data-insert-after="#answer-link"
                    defer
                ></Script>
            )}
            <Container>
                <Title id={INSERT_ID}>{'Questions & Answers'}</Title>
            </Container>
        </div>
    );
};
